/**
 * Motion tokens + recipes (see screenshots/15-spec-motion.png).
 * Keep every animation in the app on these values so motion feels consistent
 * and reduced-motion can be honoured in one place.
 */

export const duration = {
  instant: 80,  // hover, press, color changes
  fast: 140,    // popovers, chips, tooltips
  base: 200,    // fades, toasts, tab content
  slow: 300,    // panels, docking, height changes
} as const;

export const easing = {
  standard: "cubic-bezier(0.2, 0, 0, 1)",
  enter: "cubic-bezier(0, 0, 0, 1)",
  exit: "cubic-bezier(0.3, 0, 1, 1)",
  // For physics-based libs (Framer Motion, react-spring, etc.)
  spring: { stiffness: 380, damping: 32 },
} as const;

/** The OS asks for reduced motion, or Settings → Appearance → Reduce motion is on (<html data-motion="reduce">, theme/runtime.ts). */
export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  (document.documentElement.getAttribute("data-motion") === "reduce" || window.matchMedia("(prefers-reduced-motion: reduce)").matches);

/** Under reduced motion every recipe collapses to a 100 ms opacity fade (live values swap without rolling). */
export const reducedFallback = { opacity: [0, 1], duration: 100 };

/** Recipes (numbers match the motion spec board). */
export const recipes = {
  // 1 Tab & page change: cross-fade, new content rises 6px
  tabChange: { from: { opacity: 0, y: 6 }, to: { opacity: 1, y: 0 }, duration: duration.base, easing: easing.standard },

  // 2 List entrance: rows fade up 8px, 30ms stagger, max 8 staggered rows
  listEntrance: { from: { opacity: 0, y: 8 }, to: { opacity: 1, y: 0 }, duration: 220, easing: easing.enter, stagger: 30, maxStaggered: 8 },

  // 3 Live pulse: ring scales 1 → 3.4 and fades, loops. ONLY looping animation in the app.
  livePulse: { ringScale: [1, 3.4], ringOpacity: [0.9, 0], duration: 1600, loop: true },

  // 4 New → seen: when a "new" row is ≥60% visible for 1s, dot fades/shrinks, tab badge decrements
  newToSeen: { visibleThreshold: 0.6, dwellMs: 1000, duration: duration.slow, easing: easing.exit },

  // 5 Follow added: chip pops in (spring, scale .92 → 1) then teal ring fades over 1.2s
  followAdded: { from: { scale: 0.92, opacity: 0 }, to: { scale: 1, opacity: 1 }, spring: easing.spring, ringFadeMs: 1200 },

  // 6 Toast: in = rise 12px + fade 180ms (enter); out = 140ms (exit). Auto-dismiss 5s, pause on hover.
  toast: { in: { from: { opacity: 0, y: 12 }, duration: 180, easing: easing.enter }, out: { duration: duration.fast, easing: easing.exit }, autoDismissMs: 5000 },

  // 7 Peek popover: 300ms hover intent, then fade + scale .98 → 1 from the hovered row. Clip autoplays muted.
  peek: { hoverIntentMs: 300, from: { opacity: 0, scale: 0.98 }, duration: duration.fast, easing: easing.enter },

  // 8 Transcript follow: highlight follows word timestamps; when the active line changes,
  //   scroll so it sits ~1/3 from the top of the panel.
  transcriptScroll: { duration: 280, easing: easing.standard, anchor: 1 / 3 },

  // 9 Hide transcript: panel translates under the video while the video widens. Animate together.
  hideTranscript: { duration: duration.slow, easing: easing.standard },

  // 10 Mini player dock: FLIP from in-page video rect to bottom-right corner rect
  miniPlayerDock: { spring: easing.spring, approxDuration: 320, size: { w: 320, h: 180 }, inset: 24 },

  // 11 Live value change: NO count-up on first paint (the KV snapshot already has the final value).
  //    When a WebSocket delta changes a number, roll old → new.
  liveValueChange: { duration: 300, easing: easing.standard, onFirstPaint: false },

  // 12 Snapshot → live: paint instantly from the KV snapshot (no spinners, no skeletons).
  //    When the socket connects, the connection badge flips to Live and changed values flash once.
  snapshotToLive: { flashMs: 600, easing: easing.standard },

  // 13 Stage timeline (meeting page → Legislation tab rows): on first render the done line fills from Filed to the
  //    current step and each dot pops in as the line reaches it (delay = duration × its share of the line).
  stageFill: { duration: duration.slow, easing: easing.standard, dot: { duration: duration.fast, easing: easing.enter, fromScale: 0.4 } },

  // ---------- Legislation tab (screenshots/41-legislation-interactions.png) ----------
  // L1 Expand "why": row stays put, panel grows beneath; factor bars grow from the zero line, biggest first
  whyExpand: { duration: 220, easing: easing.standard, bars: { duration: duration.slow, stagger: 40, order: "byAbsEffect" } },
  // L2 Live forecast change: marker slides, number rolls (liveValueChange), "+6 pts · reason" tag fades after 4s
  forecastMove: { marker: 400, easing: easing.standard, tagHoldMs: 4000, tagFade: duration.base },
  // L3 Their way ↔ Your way: markers travel to the mirrored position across 50%; chip labels crossfade; sort kept
  perspectiveFlip: { marker: 350, easing: easing.standard, labelCrossfade: 150 },
  // L4 Client switch: rows FLIP to their new rank (no reload), meters animate to new values
  flipReorder: { duration: 260, easing: easing.standard, meters: 400 },
  // L5 Projection hover: hovered group stays, everything else dims; tooltip after a short delay
  dimOthers: { duration: 120, dimOpacity: 0.35, tooltipDelay: 80 },
  // L6 What-if: tally rolls, changed rows get a 2px warning accent; nothing saved
  whatIf: { tally: duration.slow, accentWidth: 2 },
  // L7 Condensed sticky header after the stepper leaves the viewport
  stickyHeader: { height: 48, duration: 180, easing: easing.enter },
  // L8 Stepper popover on hover/focus; click → Timeline tab, scroll, flash entry
  stepperPopover: { duration: 120, scroll: duration.slow, flashMs: 600 },
  // L9 Forecast becomes the record: meter → YEA/NAY chip per row, then a toast with result vs forecast
  recordReveal: { stagger: 60, swap: duration.base, toast: 180 },
  // L11 Compare versions: synced scroll, jump between changes
  changeJump: { duration: duration.slow, flashMs: 600 },
  // L12 Tabs: prefetch on hover intent, underline slides, changed counts pulse once
  tabs: { prefetchHoverMs: 100, underline: duration.base, countPulse: 400 },

  // ---------- Overview builder (screenshots/74) ----------
  // O1 Enter customize: grid columns fade in, toolbar swaps; nothing moves
  customizeEnter: { grid: duration.base, toolbar: 180, easing: easing.enter },
  // O2 Drag: lift on pickup, placeholder snaps to the nearest free cells, blocking widgets FLIP aside
  dragLift: { scale: 1.03, duration: 120, shadow: "0 18px 40px var(--shadow-20)" },
  dropSnap: { duration: 120, easing: easing.standard },
  gridReflow: { duration: 220, easing: easing.standard },          // uses flipFrom()
  touchPickupMs: 300,                                               // long-press on touch
  // O3/O4 Resize: snap per cell; S/M/L layout swap crossfades
  sizeCrossfade: { duration: 150 },
  // O5 Remove: shrink + fade, then the gap closes; Undo toast 5s
  widgetRemove: { scale: 0.96, duration: 160, collapse: 220, undoMs: 5000 },
  // O6 Charts: bars grow from 0 only when a chart is first ADDED (never on first paint);
  //    live updates morph lengths and roll numbers (liveValueChange)
  chartEnter: { duration: duration.slow, easing: easing.standard },
  chartMorph: { duration: duration.slow, easing: easing.standard },
  // O9 Autosave: debounce then "Saving… → Saved" crossfade
  autosave: { debounceMs: 600, chip: 150, historySteps: 50 },

  // ---- Chart settings popup + themes (screenshots/81, behaviors 1–12) ----
  // 1 Hover preview card: 250ms intent, card fades in 140ms with a 4px rise, its chart grows 300ms with 40ms stagger
  typeCard: { hoverIntentMs: 250, fade: duration.fast, rise: 4, grow: duration.slow, stagger: 40 },
  // 2 Moving across types: content crossfades 120ms, no second delay
  typeSwap: { crossfade: 120 },
  // 3 Try it in the widget (Space): universalTransition 300ms; Esc reverts with the same morph
  typeTry: { morph: duration.slow },
  // 4 Drag a field: lift 120ms at 1.03, drop snap 120ms, preview morphs 300ms after the rows arrive
  fieldDrag: { lift: 120, scale: 1.03, snap: 120, morph: duration.slow },
  // 5 Edit labels on the chart: field opens in 100ms, the Labels field pulses 600ms
  labelEdit: { open: 100, pulse: 600 },
  // 6 Colors preview on hover: instant preview, leaving reverts in 120ms, checks update in 150ms
  colorHover: { revert: 120, checks: 150 },
  // 7 Theme from the header: preview via setTheme, 200ms color crossfade, toast 5s with Undo
  themeSwitch: { crossfade: duration.base, toastMs: 5000 },
  // 8 Live data stays calm: chartMorph 300ms, number roll 300ms, re-rank FLIP 220ms
  liveCalm: { morph: duration.slow, roll: duration.slow, rerank: 220 },
  // 9 Click to filter the overview: ring fades 600ms
  crossFilter: { ring: 600 },
  // 10 Doesn't fit: the reason shows after 300ms and fades in 140ms (no shake, no red)
  typeNeeds: { delay: 300, fade: duration.fast },
  // 11 Undo, redo and drafts: autosave above (600ms, chip 150ms, 50 steps)
  // 12 Keyboard + screen readers: focus ring 2px; every preview change is announced

  // ---- Civic Pass on the lanyard (screenshots/98; physics numbers live in auth/lanyardPhysics.ts) ----
  // Pin fly-in 380ms with one overshoot, click 60ms; field print 16ms/char; completed field nudges 140px/s;
  // issued: foil sweep 900ms once, stamp 200ms; strap snap-back ≈ 420ms with ~8px overshoot.
  civicPass: { flyIn: 380, flyEasing: "cubic-bezier(.2,.9,.3,1.2)", click: 60, printPerChar: 16, print: duration.base, nudge: 140, foil: 900, stamp: 200, snap: 420 },
} as const;

/**
 * The spring as a CSS/WAAPI easing: simulates easing.spring (unit mass) from 0 to 1 and samples it
 * into linear(…), with the time it takes to settle. Used by the mini player dock (recipe 10).
 */
export function springEasing(spring: { stiffness: number; damping: number } = easing.spring): { easing: string; duration: number } {
  const dt = 1 / 120;
  let x = 0, v = 0, t = 0;
  const points: number[] = [0];
  while (t < 2 && (Math.abs(1 - x) > 0.001 || Math.abs(v) > 0.01)) {
    v += (spring.stiffness * (1 - x) - spring.damping * v) * dt;
    x += v * dt;
    t += dt;
    if (Math.round(t / dt) % 2 === 0) points.push(Math.round(x * 1000) / 1000);
  }
  points.push(1);
  return { easing: `linear(${points.join(", ")})`, duration: Math.round(t * 1000) };
}

/**
 * FLIP helper for re-ranking lists in place (client switch, alignment re-sort).
 * Call `const done = flipFrom(container)` BEFORE the DOM updates, then `done()` after.
 * Rows need a stable `data-flip-key`. Skipped under reduced motion.
 */
export function flipFrom(container: HTMLElement) {
  const first = new Map<string, DOMRect>();
  container.querySelectorAll<HTMLElement>("[data-flip-key]").forEach((el) => first.set(el.dataset.flipKey!, el.getBoundingClientRect()));
  return () => {
    if (prefersReducedMotion()) return;
    container.querySelectorAll<HTMLElement>("[data-flip-key]").forEach((el) => {
      const a = first.get(el.dataset.flipKey!);
      if (!a) return;
      const dy = a.top - el.getBoundingClientRect().top;
      if (!dy) return;
      el.animate([{ transform: `translateY(${dy}px)` }, { transform: "none" }],
        { duration: recipes.flipReorder.duration, easing: recipes.flipReorder.easing });
    });
  };
}
